import { useEffect, useRef, useState } from 'react';
import { taskAPI } from '../lib/api';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  Paperclip, Mic, Send, X, Square, Pause, Play, Trash2, Loader2, Download,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * D87 Feature 2 — Rich comment composer for task review/feedback.
 *
 * - Text input + send (delegates to onTextSend or uploads if attachment queued)
 * - Paperclip → arbitrary file (image / video / document)
 * - Mic → in-browser voice recording via MediaRecorder
 *
 * BUG/FEATURE 2 (2026-06-04) — Full recorder controls:
 *   record → pause/resume → stop → preview/listen → Send or Discard.
 * User can hear the recording before sending. State machine:
 *   idle → recording → paused (toggle) → stopped → previewing → (sent | discarded)
 */
export function TaskRichCommentBox({
  workspaceId, taskId, submissionId = null, onPosted,
  // 2026-07-13 — version-thread reuse: optional overrides route posts to a
  // different endpoint (submission comments) while keeping the same UI.
  onSendText = null, onSendFile = null,
  placeholder = null, disabled = false, inputTestId = null,
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  // Recorder state: 'idle' | 'recording' | 'paused' | 'preview'
  const [recState, setRecState] = useState('idle');
  const [recordSecs, setRecordSecs] = useState(0);
  // Captured blob + preview metadata
  const [previewBlob, setPreviewBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewMime, setPreviewMime] = useState('');
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordChunksRef = useRef([]);
  const recordTimerRef = useRef(null);
  const recordStreamRef = useRef(null);

  // Clean up the preview blob URL when the component unmounts or the
  // preview is replaced. Without this, repeated record/discard cycles leak
  // memory.
  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const stopTimer = () => {
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
  };

  const releaseStream = () => {
    const s = recordStreamRef.current;
    if (s) {
      try { s.getTracks().forEach((t) => t.stop()); } catch (_) { /* noop */ }
      recordStreamRef.current = null;
    }
  };

  const sendText = async () => {
    if (!text.trim() || disabled) return;
    setBusy(true);
    try {
      if (onSendText) await onSendText(text.trim());
      else await taskAPI.addComment(workspaceId, taskId, text.trim(), null, submissionId);
      setText('');
      toast.success('Comment posted');
      onPosted && onPosted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Failed to post');
    } finally {
      setBusy(false);
    }
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    await uploadFile(file, '');
  };

  const uploadFile = async (file, kind) => {
    if (file.size > 100 * 1024 * 1024) {
      toast.error('File too large (max 100 MB)');
      return false;
    }
    setBusy(true);
    try {
      if (onSendFile) {
        await onSendFile(file, kind, text.trim());
      } else {
        await taskAPI.uploadCommentAttachment(workspaceId, taskId, file, {
          kind,
          text: text.trim(),
          submissionId,
        });
      }
      setText('');
      toast.success(kind === 'voice' ? 'Voice note saved' : 'Attachment saved');
      onPosted && onPosted();
      return true;
    } catch (e) {
      console.error('[comment-upload] failed:', e);
      toast.error(e?.response?.data?.detail || e?.message || 'Could not save — please try again');
      return false;
    } finally {
      setBusy(false);
    }
  };

  // 2026-07-29 — never lose an in-flight comment/voice upload on navigation.
  useEffect(() => {
    if (!busy) return;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [busy]);

  // Pick a MIME type the browser AND the playback path will both handle.
  // Safari only ships mp4/aac for MediaRecorder; Chrome/Firefox prefer
  // webm/opus. We probe in priority order and fall back to the browser
  // default if none match.
  const pickMimeType = () => {
    const candidates = [
      'audio/mp4',                  // Safari (AAC)
      'audio/mp4;codecs=mp4a.40.2', // explicit AAC-LC
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
    ];
    const MR = typeof window !== 'undefined' && window.MediaRecorder;
    if (MR && typeof MR.isTypeSupported === 'function') {
      for (const c of candidates) {
        try { if (MR.isTypeSupported(c)) return c; } catch (_) { /* noop */ }
      }
    }
    return '';
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordStreamRef.current = stream;
      const mimeType = pickMimeType();
      const rec = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      recordChunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size) recordChunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        stopTimer();
        const usedMime = rec.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(recordChunksRef.current, { type: usedMime });
        releaseStream();
        // Drop straight into preview state. The user MUST be able to
        // listen back before deciding to Send or Discard.
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        const url = URL.createObjectURL(blob);
        setPreviewBlob(blob);
        setPreviewMime(usedMime);
        setPreviewUrl(url);
        setRecState('preview');
      };
      rec.start();
      mediaRecorderRef.current = rec;
      setRecState('recording');
      setRecordSecs(0);
      recordTimerRef.current = setInterval(() => setRecordSecs((s) => s + 1), 1000);
    } catch (_e) {
      toast.error('Microphone permission denied');
    }
  };

  const pauseRecording = () => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state === 'recording' && typeof rec.pause === 'function') {
      try {
        rec.pause();
        stopTimer();
        setRecState('paused');
      } catch (_) {
        toast.error('Pause not supported in this browser');
      }
    }
  };

  const resumeRecording = () => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state === 'paused' && typeof rec.resume === 'function') {
      try {
        rec.resume();
        recordTimerRef.current = setInterval(() => setRecordSecs((s) => s + 1), 1000);
        setRecState('recording');
      } catch (_) {
        toast.error('Resume not supported in this browser');
      }
    }
  };

  const stopRecording = () => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== 'inactive') {
      // .stop() fires onstop → preview state.
      rec.stop();
    } else {
      stopTimer();
      releaseStream();
      setRecState('idle');
    }
  };

  const discardRecording = () => {
    // From any recorder state, throw the captured blob away and reset.
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== 'inactive') {
      // Suppress the preview-setting onstop.
      rec.onstop = () => {
        stopTimer();
        releaseStream();
      };
      try { rec.stop(); } catch (_) { /* noop */ }
    }
    stopTimer();
    releaseStream();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewBlob(null);
    setPreviewMime('');
    setRecordSecs(0);
    setRecState('idle');
  };

  const sendPreview = async () => {
    if (!previewBlob) return;
    // Pick a sensible file extension from the mime so the server stores a
    // file the browser will know how to play back later.
    const mime = previewMime || previewBlob.type || 'audio/webm';
    let ext = 'webm';
    if (mime.includes('mp4')) ext = 'm4a';
    else if (mime.includes('ogg')) ext = 'ogg';
    else if (mime.includes('webm')) ext = 'webm';
    else if (mime.includes('wav')) ext = 'wav';
    const file = new File([previewBlob], `voice-${Date.now()}.${ext}`, { type: mime });
    const ok = await uploadFile(file, 'voice');
    // 2026-07-29 — only discard the recording after a CONFIRMED save.
    // On failure the preview bar stays so the voice note is never lost.
    if (!ok) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewBlob(null);
    setPreviewMime('');
    setRecordSecs(0);
    setRecState('idle');
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  if (recState === 'recording' || recState === 'paused') {
    return (
      <div className="flex gap-2 items-center" data-testid="d87-recording-bar">
        <div className="flex-1 flex items-center gap-2 px-3 h-8 rounded-md border border-gold/50 bg-gold/[0.06]">
          <span className="relative flex h-2 w-2">
            {recState === 'recording' && (
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
            )}
            <span
              className={`relative inline-flex rounded-full h-2 w-2 ${
                recState === 'recording' ? 'bg-red-500' : 'bg-amber-400'
              }`}
            ></span>
          </span>
          <span className="text-[12px] font-medium">
            {recState === 'recording' ? 'Recording' : 'Paused'} {fmt(recordSecs)}
          </span>
        </div>
        {recState === 'recording' ? (
          <Button
            size="sm" variant="outline" className="h-8 text-[11px]"
            onClick={pauseRecording} disabled={busy}
            data-testid="d87-record-pause"
            title="Pause"
          >
            <Pause className="h-3.5 w-3.5" />
          </Button>
        ) : (
          <Button
            size="sm" variant="outline" className="h-8 text-[11px]"
            onClick={resumeRecording} disabled={busy}
            data-testid="d87-record-resume"
            title="Resume"
          >
            <Play className="h-3.5 w-3.5" />
          </Button>
        )}
        <Button
          size="sm" className="h-8 text-[11px]"
          onClick={stopRecording} disabled={busy}
          data-testid="d87-record-stop"
          title="Stop & preview"
        >
          <Square className="h-3.5 w-3.5 mr-1" /> Stop
        </Button>
        <Button
          size="sm" variant="outline" className="h-8 text-[11px]"
          onClick={discardRecording} disabled={busy}
          data-testid="d87-record-cancel"
          title="Discard"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    );
  }

  if (recState === 'preview' && previewUrl) {
    return (
      <div className="flex gap-2 items-center flex-wrap" data-testid="d87-preview-bar">
        <audio
          controls
          src={previewUrl}
          className="h-8 flex-1 min-w-[180px]"
          data-testid="d87-preview-audio"
        />
        <span className="text-[11px] text-muted-foreground/70 shrink-0">
          {fmt(recordSecs)}
        </span>
        <Button
          size="sm" className="h-8 text-[11px]"
          onClick={sendPreview} disabled={busy}
          data-testid="d87-preview-send"
        >
          {busy ? (<><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Saving…</>) : (<><Send className="h-3.5 w-3.5 mr-1" /> Send</>)}
        </Button>
        <Button
          size="sm" variant="outline" className="h-8 text-[11px]"
          onClick={discardRecording} disabled={busy}
          data-testid="d87-preview-discard"
          title="Discard"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex gap-2 items-center" data-testid="d87-rich-comment-box">
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={onFilePicked}
        data-testid="d87-file-input"
      />
      <Button
        size="sm"
        variant="ghost"
        className="h-8 w-8 p-0 shrink-0"
        title="Attach file / image / video"
        onClick={() => fileInputRef.current?.click()}
        disabled={busy || disabled}
        data-testid="d87-attach-btn"
      >
        <Paperclip className="h-4 w-4" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        className="h-8 w-8 p-0 shrink-0"
        title="Record voice note"
        onClick={startRecording}
        disabled={busy || disabled}
        data-testid="d87-mic-btn"
      >
        <Mic className="h-4 w-4" />
      </Button>
      <Input
        placeholder={placeholder || 'Add a comment, attach a file, or record a voice note…'}
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="flex-1 h-8 text-[12px]"
        onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendText()}
        disabled={busy || disabled}
        data-testid={inputTestId || 'd87-comment-text'}
      />
      <Button size="sm" className="h-8 text-[11px]" onClick={sendText} disabled={busy || disabled || !text.trim()} data-testid="d87-send-text">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
      </Button>
    </div>
  );
}


/**
 * Authenticated <audio>/<video>/<img> wrapper.
 *
 * BUG 3 (2026-06-04) — native tags can't attach Authorization headers, so
 * the backend accepts the JWT via a `?t=<jwt>` query token.
 *
 * P0 2026-07-30 — TWO hardening fixes:
 *   1. Stored attachment URLs are ABSOLUTE with whatever APP_URL was at
 *      upload time (old preview domains, custom domains…). We now keep ONLY
 *      the path and serve from the current backend origin — a stable,
 *      authenticated, non-expiring endpoint.
 *   2. A dead player is never shown: on media error we render an
 *      "Attachment unavailable — Retry / Download" chip instead.
 */
function AuthedMedia({ url, mime, kind, fileName, testId }) {
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  if (!url) return null;
  const token = (typeof window !== 'undefined' && localStorage.getItem('stellar_token')) || '';
  // Rebuild against the CURRENT backend origin from the stored path.
  let path = url;
  try {
    if (url.startsWith('http')) {
      const u = new URL(url);
      path = u.pathname + (u.search || '');
    }
  } catch (_) { /* keep as-is */ }
  const baseUrl = path.startsWith('http') ? path : `${process.env.REACT_APP_BACKEND_URL}${path}`;
  const sep = baseUrl.includes('?') ? '&' : '?';
  const authedUrl = token ? `${baseUrl}${sep}t=${encodeURIComponent(token)}` : baseUrl;
  const bustedUrl = attempt ? `${authedUrl}&r=${attempt}` : authedUrl;
  const downloadUrl = `${authedUrl}&download=1`;

  const onMediaError = (e) => {
    console.error(`[attachment] ${kind} failed to load:`, baseUrl, e?.currentTarget?.error);
    setFailed(true);
  };

  if (failed) {
    return (
      <div
        className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-amber-500/40 bg-amber-500/[0.06] text-[11px]"
        data-testid={`${testId}-unavailable`}
      >
        <span className="text-amber-400">Attachment unavailable</span>
        <button
          type="button"
          className="underline text-foreground/80 hover:text-gold"
          onClick={() => { setFailed(false); setAttempt(a => a + 1); }}
          data-testid={`${testId}-retry`}
        >
          Retry
        </button>
        <a href={downloadUrl} target="_blank" rel="noreferrer" className="underline text-foreground/80 hover:text-gold" data-testid={`${testId}-download`}>
          Download
        </a>
      </div>
    );
  }

  if (kind === 'audio') {
    return (
      <span className="inline-flex items-center gap-1.5 max-w-full">
        <audio
          controls
          preload="metadata"
          src={bustedUrl}
          className="h-9 max-w-full"
          data-testid={testId}
          onError={onMediaError}
        />
        <a
          href={downloadUrl}
          target="_blank"
          rel="noreferrer"
          title={`Download ${fileName || 'voice note'}`}
          className="text-muted-foreground/50 hover:text-gold shrink-0"
          data-testid={`${testId}-dl`}
        >
          <Download className="h-3.5 w-3.5" />
        </a>
      </span>
    );
  }
  if (kind === 'video') {
    return (
      <video
        controls
        preload="metadata"
        src={bustedUrl}
        className="max-h-60 max-w-[320px] rounded-md border border-border/40 bg-black"
        data-testid={testId}
        onError={onMediaError}
      />
    );
  }
  // image fallback
  return (
    <img
      src={bustedUrl}
      alt={fileName || 'attachment'}
      className="max-h-40 max-w-[260px] rounded-md border border-border/40 object-cover"
      loading="lazy"
      data-testid={testId}
      onError={onMediaError}
    />
  );
}


/**
 * Inline renderer for a single comment's attachments.
 * Image → <img>; video → <video controls>; voice → <audio controls>; document → download chip.
 *
 * BUG 3 (2026-06-04) — image / video / voice are now rendered via
 * AuthedMedia so the JWT-protected download endpoint can serve them.
 * Documents still use a plain `<a>` with target=_blank because the user
 * will be re-authenticated by the browser session on the new tab.
 */
export function TaskCommentAttachments({ attachments }) {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-2" data-testid="d87-comment-attachments">
      {attachments.map((a) => {
        const url = a.url || '#';
        if (a.message_type === 'image') {
          return (
            <AuthedMedia
              key={a.id}
              url={url}
              mime={a.mime}
              kind="image"
              fileName={a.file_name}
              testId="d87-attachment-image"
            />
          );
        }
        if (a.message_type === 'video') {
          return (
            <AuthedMedia
              key={a.id}
              url={url}
              mime={a.mime}
              kind="video"
              fileName={a.file_name}
              testId="d87-attachment-video"
            />
          );
        }
        if (a.message_type === 'voice') {
          return (
            <AuthedMedia
              key={a.id}
              url={url}
              mime={a.mime}
              kind="audio"
              fileName={a.file_name}
              testId="d87-attachment-audio"
            />
          );
        }
        return (
          <a
            key={a.id}
            href={url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-border/40 bg-foreground/[0.03] text-[11px] hover:bg-foreground/[0.06]"
            data-testid="d87-attachment-doc"
          >
            <Paperclip className="h-3 w-3" /> {a.file_name}
            <span className="text-muted-foreground">
              · {(a.size / 1024).toFixed(0)} KB
            </span>
          </a>
        );
      })}
    </div>
  );
}
